import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import NumberFormat from 'react-number-format';
import {TFunction} from 'i18next';
import {ApplicationState} from '../../../../store';
import {ControlsSnapshot} from '../../dashboard-controls/controls-snapshot';
import {
    DateRange,
    DateRangeWithFunctionalLocationsRequest,
    fetchAvoidableBreakdownsKPIRequest,
} from '../../../../store/analytics';
import {buildRequest, isValidSnapshot} from './request-builder';
import styles from './avoidable-breakdowns-widget.module.scss';
import WidgetLoading from '../widget-loading';
import {NumberFormatter} from '../kpi-box/kpi-meta-store';
import {ParamKey} from '../../../../store/dashboard';
import {toBetweenMonthRangeDisplay} from '../../dashboard-controls/date-range-selector/helpers/date-range-helper';
import {AvoidableBreakdownsKPIData} from '../../../../store/analytics/types/key-performance-indicator.types';


class AvoidableBreakdownsWidget extends React.Component<AllProps, AllState> {
    public constructor(props) {
        super(props);

        this.state = {
            avoidableBreakdowns: 0,
        }
    }

    public componentDidMount(): void {
        const { controlsSnapshot, dispatchAvoidableBreakdownsRequest } = this.props;
        if (controlsSnapshot && isValidSnapshot(controlsSnapshot)) {
            dispatchAvoidableBreakdownsRequest(buildRequest(controlsSnapshot));
        }
    }

    public componentDidUpdate(prevProps: Readonly<AllProps>): void {
        const { controlsSnapshot, dispatchAvoidableBreakdownsRequest, data } = this.props;
        if (controlsSnapshot && !controlsSnapshot.equals(prevProps.controlsSnapshot) && isValidSnapshot(controlsSnapshot)) {
            dispatchAvoidableBreakdownsRequest(buildRequest(controlsSnapshot));
        }

        if (data && data !== prevProps.data) {
            this.setState({
                avoidableBreakdowns: data.avoidableBreakdownPercentage * data.totalBreakdowns,
            })
        }
    }

    public render(): JSX.Element {
        const { t, controlsSnapshot, data } = this.props;
        const { avoidableBreakdowns } = this.state;

        const formatter = new NumberFormatter('.', ',', false, 0);

        return (
            <div className={styles.widgetContainer}>
                {data ? (
                    <div className={styles.chartView}>
                        <div className={styles.chartColumn}>
                            <div className={styles.titlePanel}>
                                {t('Avoidable Breakdowns').toUpperCase()}
                            </div>
                            <div className={styles.valuePanel}>
                                <div className={styles.value}>
                                    <NumberFormat
                                        value={avoidableBreakdowns}
                                        displayType='text'
                                        decimalSeparator={formatter.decimalSeparator}
                                        thousandSeparator={formatter.thousandsSeparator}
                                        fixedDecimalScale={formatter.fixedDecimalScale}
                                        decimalScale={formatter.decimalScale}
                                    />
                                    <span className={styles.unit}>#</span>
                                </div>
                                <div className={styles.label}>
                                    {this.getSubtitle(controlsSnapshot, t)}
                                </div>
                            </div>
                        </div>
                        <div className={styles.chartColumn}>
                            <div className={styles.infoSecondary}>
                                <div className={styles.labelSecondary}>{t('Total Breakdowns')}</div>
                                <div className={styles.valueSecondary}>
                                    <NumberFormat
                                        value={data.totalBreakdowns}
                                        displayType='text'
                                        decimalSeparator={formatter.decimalSeparator}
                                        thousandSeparator={formatter.thousandsSeparator}
                                        fixedDecimalScale={formatter.fixedDecimalScale}
                                    /><span className={styles.unit}>#</span>
                                </div>
                            </div>
                            <div className={styles.infoSecondary}>
                                <div className={styles.labelSecondary}>{t('Avoidable Breakdowns')}</div>
                                <div className={styles.valueSecondary}>
                                    <NumberFormat
                                        value={avoidableBreakdowns}
                                        displayType='text'
                                        decimalSeparator={formatter.decimalSeparator}
                                        thousandSeparator={formatter.thousandsSeparator}
                                        fixedDecimalScale={formatter.fixedDecimalScale}
                                        decimalScale={formatter.decimalScale}
                                    />
                                    <span className={styles.unit}>#</span>
                                </div>
                            </div>
                            <div className={styles.infoText}>{t('Studies have show that we can avoid {{data}}% of critical tire breakdown by equipping your fleet with TPMS', {data: data.avoidableBreakdownPercentage * 100})}</div>
                        </div>
                    </div>
                ) : (
                    <WidgetLoading />
                )}
            </div>
        )
    }

    private getSubtitle(controlsSnapshot: ControlsSnapshot, t: TFunction): string {
        if (controlsSnapshot.has(ParamKey.InspectionPeriod)) {
            const dateRange = controlsSnapshot.getValueForParam<DateRange>(ParamKey.InspectionPeriod);
            return toBetweenMonthRangeDisplay(dateRange, t);
        }
        return '';
    }
}

const mapStateToProps = ({analytics}: ApplicationState) => ({
    data: analytics.avoidableBreakdownsKPI,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    dispatchAvoidableBreakdownsRequest: (request: DateRangeWithFunctionalLocationsRequest) =>
        dispatch(fetchAvoidableBreakdownsKPIRequest(request)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AvoidableBreakdownsWidget));

interface PropsFromState {
    data: AvoidableBreakdownsKPIData;
}

interface PropsFromDispatch {
    dispatchAvoidableBreakdownsRequest: typeof fetchAvoidableBreakdownsKPIRequest;
}

export interface OwnProps {
    controlsSnapshot: ControlsSnapshot;
}

type AllProps = OwnProps & WithTranslation & PropsFromState & PropsFromDispatch;

export interface OwnState {
    avoidableBreakdowns: number;
}

type AllState = OwnState;
